import {
	extractRejectionReason,
	getSafeUserErrorMessage,
	logApplicationError,
} from '@/utils/safeErrorMessage';

export class BaseLoader {
	_store = {};
	_dispatch = () => {};
	constructor(store) {
		this._store = store;
		this._dispatch = store.dispatch;
	}

	// Loaders run as soon as the router is created — on a page refresh that is before the
	// Firebase session exists, so requests would go out without an Authorization header.
	_waitForFirebaseReady = (signal) =>
		new Promise((resolve, reject) => {
			if (this._store.getState().firebase.firebaseReady) {
				resolve();
				return;
			}
			const unsubscribe = this._store.subscribe(() => {
				if (this._store.getState().firebase.firebaseReady) {
					unsubscribe();
					resolve();
				}
			});
			signal.addEventListener('abort', () => {
				unsubscribe();
				reject(signal.reason);
			});
		});

	_loader = async (endpoint, request, query, queryOptions) => {
		await this._waitForFirebaseReady(request.signal);
		const promise = this._store.dispatch(endpoint.initiate(query, queryOptions));
		request.signal.onabort = promise.abort;
		const res = await promise;
		const { isError, error } = res;
		if (isError) {
			// fetchBaseQuery reports failures without a usable HTTP status as a string (e.g.
			// 'PARSING_ERROR', 'FETCH_ERROR'), which Response rejects; fall back to 500.
			const httpStatus = error.originalStatus ?? error.status ?? 403;
			const status =
				Number.isInteger(httpStatus) && httpStatus >= 200 && httpStatus <= 599
					? httpStatus
					: 500;
			logApplicationError('Route loader request failed', { status, error });

			throw new Response(
				JSON.stringify({
					status: 'rejected',
					reason: extractRejectionReason(error),
				}),
				{
					status,
					statusText: getSafeUserErrorMessage({ status }),
				},
			);
		}
		return res.data;
	};
}
